import { Box, MenuItem, TextField } from '@mui/material';
import { ReactComponent as StarsIcon } from '../../../assets/icons/AI.svg';
import Popover from 'components/common/Popover';
import api from 'api';
import { useSelector } from 'react-redux';
import { useState, useRef, useEffect } from 'react';
import useEnabledCountries from 'hooks/useEnabledCountries';
import { countryLabelKey, cvUploadCountryCode } from 'utils/countries';
import type { CountryCode } from 'types/countries';
import * as S from '../Forms.styled';
import { useTranslation } from 'react-i18next';
import { RootState } from 'redux/store';
import { useParams } from 'react-router-dom';
import AlertMessage, { AlertObj } from 'components/common/alert';
import { emitInitialDocumentsRefresh } from 'views/Dashboards-B2B/components/BACKOFFICEFLOW/CLIENTVIEW/components/ClientProfile/components/InitialDocuments/initialDocumentsEvents';

export default function UploadCVButton() {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [colorCycle, setColorCycle] = useState(true);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isShowingAlert, setShowingAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState<AlertObj>({
        type: 'info',
        value: '',
    });

    const params = useParams();
    const candidate_id = Number(params.id) || null;
    const { codes } = useEnabledCountries();
    const [selectedCountry, setSelectedCountry] = useState<CountryCode | null>(null);
    const [countryError, setCountryError] = useState(false);
    const countryCode = cvUploadCountryCode(codes, selectedCountry);
    const needsCountryChoice = codes.length > 1;

    // Cycle colors while loading
    useEffect(() => {
        if (loading) {
            const interval = setInterval(() => {
                setColorCycle((prev) => !prev);
            }, 500); // Change color every 500ms

            return () => clearInterval(interval);
        } else {
            setColorCycle(true); // Reset to default when not loading
        }
    }, [loading]);

    // opens the file picker
    const handleButtonClick = () => {
        if (!countryCode) {
            setCountryError(needsCountryChoice);
            return;
        }
        fileInputRef.current?.click();
    };

    // runs after a file is selected
    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;

        const file = e.target.files[0];
        if (!countryCode) return;
        setLoading(true);

        try {
            await api.tableInput.postCV(file, candidate_id, countryCode);
            setAlertMessage({ type: 'success', value: t('CVSuccess', { lng: language }) });
            setShowingAlert(true);
            // Notify the Initial Documents card (right-rail / onboarding
            // sidebar) that the resume row needs to refetch — postCV writes
            // a Resume document for this candidate on the backend, so the
            // card's "Provided / Missing" state would otherwise be stale.
            emitInitialDocumentsRefresh({ userId: candidate_id });
        } catch {
            setAlertMessage({ type: 'error', value: t('somethingWentWrong', { lng: language }) });
            setShowingAlert(true);
        } finally {
            setLoading(false);
            e.target.value = '';
        }
    };

    return (
        <>
            <S.CVInfoContainer>
                <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx" // optional
                />
                {codes.length > 1 && (
                    <TextField
                        select
                        required
                        size="small"
                        label={t('selectCountry', { lng: language })}
                        value={selectedCountry ?? ''}
                        error={countryError}
                        helperText={t('cvDestinationHelper', { lng: language })}
                        onChange={(event) => {
                            const value = event.target.value;
                            setCountryError(false);
                            setSelectedCountry(codes.find((code) => code === value) ?? null);
                        }}
                        sx={{ minWidth: 180, mr: 1 }}
                    >
                        {codes.map((code) => (
                            <MenuItem key={code} value={code}>
                                {t(countryLabelKey(code), { lng: language })}
                            </MenuItem>
                        ))}
                    </TextField>
                )}
                <S.AutoFillButton
                    type="button"
                    onClick={handleButtonClick}
                    disabled={loading}
                    $loading={loading}
                    $cycle={colorCycle}
                >
                    <Box
                        component="span"
                        aria-hidden="true"
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            color: '#282828',
                            height: '18px',
                            width: '18px',
                        }}
                    >
                        <StarsIcon />
                    </Box>
                    {loading
                        ? t('loading', { lng: language })
                        : t('autoFillForm', { lng: language })}
                </S.AutoFillButton>
                <Popover
                    translationText={t('uploadCVText', { lng: language })}
                    iconHeight="23px"
                    iconWidth="23px"
                    iconColor="#8353E2FF"
                    popoverStyle={{
                        border: '1px solid #8353E2',
                        borderRadius: '6px',
                        boxShadow:
                            '0px 0px 2px rgba(23, 26, 31, 0.12), 0px 8px 17px rgba(23, 26, 31, 0.15)',
                        width: '320px',
                        padding: '12px',
                    }}
                />
            </S.CVInfoContainer>
            <AlertMessage
                type={alertMessage?.type}
                text={alertMessage?.value}
                open={isShowingAlert}
                onClose={() => setShowingAlert(false)}
            />
        </>
    );
}
